/**
 * A submission's version history + the student's RESUBMISSION box (M3).
 *
 * Local-first over `submission_version` (mine via my_cohorts_student). Each
 * version shows its body/files and, if reviewed, the tutor's feedback and
 * the result (returned/approved/rejected). When the latest was RETURNED and the
 * policy allows it, resubmitting is offered (new version).
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { CheckCircle2, RotateCcw, XCircle } from "@fleet/icons";
import { canResubmit, type ReviewPolicy, submitNewVersionMutation } from "@space/shared";
import type { SubmissionVersion } from "@space/powersync";
import { useI18n } from "@/providers/i18n-provider";
import { useSession } from "@/lib/auth";
import { useDrizzle } from "@/hooks/use-drizzle";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { Skeleton } from "@fleet/ui";
import { useSubmissionVersions } from "./hooks";
import { useEffectiveReviewPolicy } from "./use-effective-policy";

function fileCount(files: string | null | undefined): number {
  if (!files) {
    return 0;
  }
  try {
    const v: unknown = JSON.parse(files);
    return Array.isArray(v) ? v.length : 0;
  } catch {
    return 0;
  }
}

function fmt(iso: string | null | undefined): string {
  if (!iso) {
    return "";
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** A version's result badge. */
const OutcomeBadge: Component<{ outcome: string | null | undefined }> = (props) => {
  const { t } = useI18n();
  return (
    <Show
      when={props.outcome}
      fallback={
        <span class="rounded-full bg-muted px-2 py-0.5 text-xs" data-testid="version-pending">
          {t(msg`Awaiting review`)}
        </span>
      }
    >
      {(o) => (
        <span
          class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
          classList={{
            "bg-success/15 text-success": o() === "approved",
            "bg-warning/15 text-warning-foreground": o() === "returned",
            "bg-destructive/15 text-destructive": o() === "rejected",
          }}
          data-testid={`version-outcome-${o()}`}
        >
          <Show when={o() === "approved"}>
            <CheckCircle2 class="size-3" />
          </Show>
          <Show when={o() === "returned"}>
            <RotateCcw class="size-3" />
          </Show>
          <Show when={o() === "rejected"}>
            <XCircle class="size-3" />
          </Show>
          <Show when={o() === "approved"}>{t(msg`Approved`)}</Show>
          <Show when={o() === "returned"}>{t(msg`Returned`)}</Show>
          <Show when={o() === "rejected"}>{t(msg`Rejected`)}</Show>
        </span>
      )}
    </Show>
  );
};

/** (Read-only) list of versions with their feedback. */
export const SubmissionHistory: Component<{
  assignmentId: string;
  cohortId: string;
  userId: string;
}> = (props) => {
  const { t } = useI18n();
  const versions = useSubmissionVersions(
    () => props.assignmentId,
    () => props.cohortId,
    () => props.userId,
  );
  return (
    <Show when={!versions.isLoading} fallback={<Skeleton class="h-24 w-full rounded-xl" />}>
      <Show
        when={(versions.data ?? []).length > 0}
        fallback={<p class="text-sm text-muted-foreground">{t(msg`No submissions yet.`)}</p>}
      >
        <ol class="space-y-3" data-testid="submission-history">
          <For each={versions.data ?? []}>
            {(v) => (
              <li
                class="rounded-xl border-2 border-border p-3"
                data-testid={`version-${v.version}`}
              >
                <div class="flex items-center justify-between gap-2">
                  <span class="text-sm font-semibold">
                    {(() => {
                      const version = v.version ?? 0;
                      return t(msg`Version ${version}`);
                    })()}
                  </span>
                  <OutcomeBadge outcome={v.outcome} />
                </div>
                <p class="mt-1 text-xs text-muted-foreground">{fmt(v.submitted_at)}</p>
                <Show when={v.body}>
                  <p class="mt-2 whitespace-pre-wrap text-sm">{v.body}</p>
                </Show>
                <Show when={fileCount(v.files) > 0}>
                  <p class="mt-1 text-xs text-muted-foreground">
                    {(() => {
                      const count = fileCount(v.files);
                      return t(msg`${count} file(s) attached`);
                    })()}
                  </p>
                </Show>
                <Show when={v.feedback}>
                  <div class="mt-2 rounded-lg bg-muted/50 p-2" data-testid="version-feedback">
                    <p class="text-xs font-semibold text-muted-foreground">
                      {t(msg`Tutor feedback`)}
                    </p>
                    <p class="whitespace-pre-wrap text-sm">{v.feedback}</p>
                  </div>
                </Show>
              </li>
            )}
          </For>
        </ol>
      </Show>
    </Show>
  );
};

/** Latest version (by number) from a list. */
function latestOf(versions: SubmissionVersion[]): SubmissionVersion | null {
  return versions.reduce<SubmissionVersion | null>((acc, v) => {
    if (!acc || (v.version ?? 0) > (acc.version ?? 0)) {
      return v;
    }
    return acc;
  }, null);
}

/**
 * RESUBMISSION box (student): visible when the latest version was RETURNED and the
 * resubmission policy allows it. Mirror of the server-side enforcement.
 */
export const ResubmitBox: Component<{
  assignmentId: string;
  cohortId: string;
  organizationId: string;
  userId: string;
}> = (props) => {
  const { t } = useI18n();
  const { userId } = useSession();
  const drizzle = useDrizzle();
  const versions = useSubmissionVersions(
    () => props.assignmentId,
    () => props.cohortId,
    () => props.userId,
  );
  const policy = useEffectiveReviewPolicy(
    () => props.assignmentId,
    () => props.organizationId,
  );
  const [body, setBody] = createSignal("");
  const [busy, setBusy] = createSignal(false);

  const latest = createMemo(() => latestOf(versions.data ?? []));
  const isFirst = createMemo(() => latest() === null);
  const gate = createMemo(() => {
    const p: ReviewPolicy = policy();
    const l = latest();
    return canResubmit(p, l ? { version: l.version ?? 0, outcome: l.outcome ?? null } : null);
  });
  // First submission (no versions) or resubmission after a RETURN.
  const canShow = createMemo(() => gate().ok && (isFirst() || latest()?.outcome === "returned"));

  const submit = async () => {
    const db = drizzle();
    if (!db || !body().trim()) {
      return;
    }
    setBusy(true);
    try {
      await submitNewVersionMutation(db, {
        assignmentId: props.assignmentId,
        cohortId: props.cohortId,
        organizationId: props.organizationId,
        userId: userId() ?? props.userId,
        body: body().trim(),
      });
      setBody("");
      showToast(isFirst() ? t(msg`Submitted for review`) : t(msg`Resubmitted`), "success");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Show
      when={canShow()}
      fallback={
        <Show when={latest()?.outcome === "returned" && !gate().ok}>
          <p class="text-sm text-muted-foreground" data-testid="resubmit-blocked">
            {t(msg`Resubmissions are closed for this assignment.`)}
          </p>
        </Show>
      }
    >
      <div class="space-y-2 rounded-xl border-2 border-primary/40 p-3" data-testid="resubmit-box">
        <p class="text-sm font-semibold">
          {isFirst() ? t(msg`Submit for review`) : t(msg`Resubmit`)}
        </p>
        <textarea
          class="min-h-24 w-full rounded-lg border-2 border-border bg-field-background p-2 text-sm focus-visible:border-primary focus-visible:outline-none"
          placeholder={
            isFirst()
              ? t(msg`Write your submission…`)
              : t(msg`Address the feedback and submit a new version…`)
          }
          value={body()}
          onInput={(e) => setBody(e.currentTarget.value)}
          data-testid="resubmit-body"
        />
        <Button onClick={submit} disabled={busy() || !body().trim()} data-testid="resubmit-submit">
          {isFirst() ? t(msg`Submit for review`) : t(msg`Send new version`)}
        </Button>
      </div>
    </Show>
  );
};
